import type { UploadPolicy } from '../types';

// Alapértelmezett biztonsági tiltólista kiterjesztések (futtatható, script, installer fájlok)
export const DEFAULT_DENIED_EXT = new Set([
  // Windows
  'exe', 'com', 'bat', 'cmd', 'msi', 'msp', 'scr', 'pif', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh',
  'ps1', 'ps1xml', 'psc1', 'cpl', 'dll', 'sys', 'drv', 'gadget', 'application', 'lnk',
  // Linux / Unix
  'sh', 'bash', 'run', 'bin', 'out', 'elf', 'deb', 'rpm', 'appimage',
  // macOS
  'app', 'command', 'workflow', 'dmg', 'pkg',
  // Platformfüggetlen veszélyes formátumok
  'jar', 'apk'
]);

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export function validateSingleFile(file: File, policy?: UploadPolicy | null): FileValidationResult {
  if (!file) {
    return { valid: false, error: 'Hiányzó fájl' };
  }

  // 1. 0 byte ellenőrzés
  if (!file.size || file.size <= 0) {
    return {
      valid: false,
      error: `„${file.name}” üres (0 byte)! 0 byte méretű fájl feltöltése nem engedélyezett.`
    };
  }

  const ext = (file.name.split('.').pop() || '').toLowerCase();

  // 2. Tiltólista (alapértelmezett + rendszerszintű (B9) + egyedi szervezeti)
  const deniedSet = new Set(DEFAULT_DENIED_EXT);
  const extraDenied = [policy?.upload_denied_ext_extra, policy?.upload_denied_ext_system].filter(Boolean).join(',');
  if (extraDenied) {
    extraDenied
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(Boolean)
      .forEach(e => deniedSet.add(e));
  }

  if (deniedSet.has(ext)) {
    return {
      valid: false,
      error: `„${file.name}” (.${ext}) biztonsági okból tiltólistán szerepel, feltöltése nem engedélyezett!`
    };
  }

  // 3. Engedélyezett kiterjesztések (ha van explicit fehérlista)
  if (policy?.upload_allowed_ext) {
    const allowed = policy.upload_allowed_ext
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
    if (allowed.length > 0 && !allowed.includes(ext)) {
      return {
        valid: false,
        error: `„${file.name}” (.${ext}) nem engedélyezett! Engedélyezett formátumok: ${allowed.join(', ')}`
      };
    }
  }

  // 4. Egyedi maximális fájlméret
  if (policy?.upload_max_size_mb) {
    const maxMb = parseFloat(policy.upload_max_size_mb);
    if (!isNaN(maxMb) && maxMb > 0 && file.size > maxMb * 1024 * 1024) {
      return {
        valid: false,
        error: `„${file.name}” mérete (${formatBytes(file.size)}) meghaladja a beállított maximum ${maxMb} MB korlátot!`
      };
    }
  }

  return { valid: true };
}

export interface QuotaCheckResult {
  isOverQuota: boolean;
  currentBytes: number;
  newFilesBytes: number;
  projectedBytes: number;
  maxBytes: number;
  maxMb: number;
  usedPercent: number;
  error?: string;
}

export function checkStorageQuota(
  currentStorageBytes: number,
  newFilesBytes: number,
  maxStorageMb: number = 500
): QuotaCheckResult {
  // 0 = korlátlan (a globális vagy a szervezeti kvóta is lehet korlátlan)
  const maxBytes = maxStorageMb > 0 ? maxStorageMb * 1024 * 1024 : 0;
  const projectedBytes = currentStorageBytes + newFilesBytes;
  const isOverQuota = maxBytes > 0 && projectedBytes > maxBytes;
  const usedPercent = maxBytes > 0 ? Math.min(100, Math.round((projectedBytes / maxBytes) * 100)) : 0;

  let error: string | undefined;
  if (isOverQuota) {
    const curMb = (currentStorageBytes / (1024 * 1024)).toFixed(1);
    const reqMb = (newFilesBytes / (1024 * 1024)).toFixed(1);
    error = `Kvótatúllépés! A feltöltéssel a szervezet elérné a tárhelykorlátot (${curMb} MB foglalt + ${reqMb} MB új fájl / MAX ${maxStorageMb} MB). Távolítson el korábbi mellékleteket vagy kérjen kvótabővítést!`;
  }

  return {
    isOverQuota,
    currentBytes: currentStorageBytes,
    newFilesBytes,
    projectedBytes,
    maxBytes,
    maxMb: maxStorageMb,
    usedPercent,
    error
  };
}
